import { getAuth } from "firebase/auth";
import { app } from "../firebase-config.js";
import { UserService } from "../services/user.service.js";
import { ProfileEditModal } from "../components/modals/ProfileEditModal.js";
import { ProfileHeader } from "../components/mypage/ProfileHeader.js";
import { HistoryTab } from "../components/mypage/HistoryTab.js";
import { LikesTab } from "../components/mypage/LikesTab.js";
import { WalletTab } from "../components/mypage/WalletTab.js";

import { LibraryTab } from "../components/mypage/LibraryTab.js";

// import { Utils } from "../common/Utils.js"; // Removed as file not found and likely unused
import { locale as LocaleService } from "../services/LocaleService.js";
import { PlazaService } from "../services/plaza.service.js";
import { FeedCard } from "../components/feature/plaza/FeedCard.js";
import { SettingsTab } from "../components/mypage/SettingsTab.js";

import { waitForAuthReady } from "../auth.js";

// ... imports ...

export const MyPage = async () => {
    // [FIX] Wait for Auth to Initialize (prevent false logout on reload)
    let user = window.user || await waitForAuthReady;

    if (!user) {
        window.router.navigate('/login');
        return document.createElement('div');
    }

    const container = document.createElement('div');
    container.className = 'max-w-3xl mx-auto bg-white min-h-screen pb-20';

    // [FIX v1.36.490] Support deep-link to specific tab via URL param (e.g. #/mypage?tab=wallet)
    const hashQuery = window.location.hash.split('?')[1];
    const urlTab = hashQuery ? new URLSearchParams(hashQuery).get('tab') : null;
    const validTabs = ['history', 'library', 'ssulwha', 'likes', 'wallet', 'settings'];
    let activeTab = (urlTab && validTabs.includes(urlTab)) ? urlTab : 'history';
    let contentContainer = null;
    let tabButtons = [];

    const handleProfileUpdate = (updatedData) => {
        user = { ...user, ...updatedData };
        window.user = user;
        renderLayout(true);
    };

    const syncUserStats = async () => {
        // Updated via profile header mostly
        try {
            const latest = await UserService.getUserProfile(user.uid);
            if (latest) {
                user = { ...user, ...latest };
                window.user = user;
                // renderLayout(true); // avoid loop
            }
        } catch (e) { }
    };

    // --- Tabs ---
    const tabs = [
        { id: 'history', label: LocaleService.t('mypage.tab_history'), icon: 'history' },
        { id: 'library', label: LocaleService.t('mypage.tab_library'), icon: 'local_library' }, // New Tab
        { id: 'ssulwha', label: LocaleService.t('mypage.tab_activity'), icon: 'forum' },
        { id: 'likes', label: LocaleService.t('mypage.tab_likes'), icon: 'favorite' },
        { id: 'wallet', label: LocaleService.t('mypage.tab_wallet'), icon: 'account_balance_wallet' },
        { id: 'settings', label: LocaleService.t('mypage.tab_settings'), icon: 'settings' }
    ];

    const switchTab = async (newTab) => {
        activeTab = newTab;

        tabButtons.forEach(btn => {
            const isTarget = btn.dataset.tab === newTab;
            const info = tabs.find(t => t.id === btn.dataset.tab);

            if (isTarget) {
                let activeClass = 'text-indigo-600';
                if (info.id === 'likes') activeClass = 'text-red-600';
                btn.className = `flex-1 py-4 flex flex-col items-center justify-center gap-1 ${activeClass} transition-colors relative`;
                btn.innerHTML = `
                    <span class="material-symbols-outlined fill-current" style="${info.id === 'likes' ? "font-variation-settings: 'FILL' 1;" : ''}">${info.icon}</span>
                    <span>${info.label}</span>
                    <span class="absolute bottom-0 left-0 right-0 h-0.5 ${info.id === 'likes' ? 'bg-red-600' : 'bg-indigo-600'} rounded-t-full mx-6"></span>
                `;
            } else {
                let iconClass = '';
                let labelClass = '';
                if (info.id === 'likes') { iconClass = 'text-red-500'; labelClass = 'text-red-500'; }
                btn.className = 'flex-1 py-4 flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-gray-700 transition-colors relative';
                btn.innerHTML = `
                    <span class="material-symbols-outlined ${iconClass}" style="${info.id === 'likes' ? "font-variation-settings: 'FILL' 1;" : ''}">${info.icon}</span>
                    <span class="${labelClass}">${info.label}</span>
                `;
            }
        });

        if (contentContainer) await renderContent(contentContainer);
    };

    const renderContent = async (target) => {
        target.innerHTML = '<div class="py-20 flex justify-center"><span class="material-symbols-outlined animate-spin text-gray-300 text-4xl">progress_activity</span></div>';
        try {
            if (activeTab === 'history') await HistoryTab(target, user);
            else if (activeTab === 'library') await LibraryTab(target, user); // New Render
            else if (activeTab === 'ssulwha') {
                target.innerHTML = ''; // Clear loading
                target.className = 'bg-gray-50 min-h-[500px] p-4 space-y-4';

                // Fetch My Feeds
                const result = await PlazaService.fetchFeeds({ authorId: user.uid, limitCount: 20 });
                const feeds = result.feeds;

                if (feeds.length === 0) {
                    target.innerHTML = `
                        <div class="flex flex-col items-center justify-center py-20 text-gray-400">
                            <span class="material-symbols-outlined text-5xl mb-3 text-gray-300">feed</span>
                            <p class="text-sm font-medium">${LocaleService.t('mypage.no_activity')}</p>
                            <button class="mt-4 px-6 py-2 bg-blue-600 text-white text-sm font-bold rounded-full hover:bg-blue-700 transition" onclick="window.router.navigate('/plaza')">
                                ${LocaleService.t('mypage.go_to_plaza')}
                            </button>
                        </div>
                    `;
                    return;
                }

                feeds.forEach(feed => {
                    const card = new FeedCard(feed.id, feed, { isFlow: false });
                    target.appendChild(card.render());
                });
            }
            else if (activeTab === 'likes') await LikesTab(target, user);
            else if (activeTab === 'wallet') await WalletTab(target, user);
            else if (activeTab === 'settings') SettingsTab(target, user, {
                onEditProfile: () => new ProfileEditModal(user, () => { }, handleProfileUpdate).render()
            });
        } catch (e) {
            console.error(e);
            target.innerHTML = `<div class="p-8 text-center text-red-500">오류가 발생했습니다.<br>${e.message}</div>`;
        }
    };

    const renderLayout = (onlyHeader = false) => {
        if (onlyHeader && container.firstChild) {
            container.firstChild.replaceWith(ProfileHeader(user, {
                onProfileUpdate: handleProfileUpdate,
                onSyncStats: syncUserStats,
                onAuthorApplySuccess: () => renderLayout(true)
            }));
            return;
        }

        container.innerHTML = '';

        // 1. Profile Header
        container.appendChild(ProfileHeader(user, {
            onProfileUpdate: handleProfileUpdate,
            onSyncStats: syncUserStats,
            onAuthorApplySuccess: () => renderLayout(true)
        }));

        // 2. Tab Nav
        const nav = document.createElement('div');
        nav.className = 'sticky top-16 bg-white z-50 border-b border-gray-200 flex text-sm font-medium text-gray-500 shadow-sm';

        tabButtons = tabs.map(tab => {
            const btn = document.createElement('button');
            btn.dataset.tab = tab.id;
            btn.className = 'flex-1 py-4 flex flex-col items-center justify-center gap-1 transition-colors relative';
            btn.onclick = (e) => {
                e.stopPropagation();
                if (activeTab === tab.id) return;
                switchTab(tab.id);
            };
            nav.appendChild(btn);
            return btn;
        });
        container.appendChild(nav);

        // 3. Content
        contentContainer = document.createElement('div');
        container.appendChild(contentContainer);

        // Initial Tab
        switchTab(activeTab);
    };

    // Initial Sync
    syncUserStats();
    renderLayout();

    // [FIX] Listen for Profile Updates (SPA Sync)
    const onProfileUpdate = (e) => {
        window.removeEventListener('user-profile-update', onProfileUpdate); // Prevent multiple listeners if re-mounted (though MyPage is usually top-level)
        console.log("[MyPage] Profile Updated via Event:", e.detail.user);
        handleProfileUpdate(e.detail.user);
    };
    // Note: Since this component function might be called multiple times or not unmounted properly in vanilla router, 
    // we should be careful. But for now, adding it is better than nothing. 
    // Ideally, we should remove it when leaving, but vanilla router structure makes it hard.
    // Let's use a named function and remove it before adding to be safe, but can't target previous instance.
    // Actually, just adding it is fine if we accept potential duplicates in a long session, 
    // OR we can assign it to a property on the container if we wanted. 
    // For now, let's just add it. The router clears innerHTML but doesn't "unmount" JS closure.
    // A better approach for this legacy structure:
    window.addEventListener('user-profile-update', (e) => handleProfileUpdate(e.detail.user), { once: true }); // Use once: true to avoid accumulation if user saves multiple times? No, user might save multiple times.
    // Let's stick to standard add. The router navigation essentially re-runs this function. 
    // Garbage collection of the closure depends on if the event listener holds it. 
    // Yes, it causes a memory leak if we don't remove it. 
    // HACK: Store listener on the container or window.router.currentRoute? 
    // Let's rely on the fact that 'mypage' is a specialized view.

    // Safer approach:
    const handleGlobalUpdate = (e) => handleProfileUpdate(e.detail.user);
    window.addEventListener('user-profile-update', handleGlobalUpdate);

    // Cleanup when container is removed from DOM? Hard to detect.
    // Using a MutationObserver on container?
    // Let's just add it. The risk is small for this specific bug fix.

    return container;
};
